// usb.js
// ✅ Web Serial API 單例封裝，支援 Usb.xxx() 直接呼叫
import { taskType } from "../../reducers/task";
import { UsbCmd, WriteScriptCmd } from "./usb-cmd";
// ======================================================
// ✅ WebYModem 內嵌版本（符合舊版輸入輸出）
// ======================================================
export class WebYModem {
    static SOH = 0x01;  // 128-byte packet
    static STX = 0x02;  // 1024-byte packet
    static EOT = 0x04;  // End of transmission
    static ACK = 0x06;  // Acknowledge
    static NAK = 0x15;  // Negative acknowledge
    static CAN = 0x18;  // Cancel
    static C = 0x43;    // ASCII 'C'

    static PACKET_LEN = 128;
    static PACKET_TOTAL_LEN = WebYModem.PACKET_LEN + 5;
    static HEADER_LEN = 128;
    static HEADER_TOTAL_LEN = WebYModem.HEADER_LEN + 5;
    static PACKET_MARK = WebYModem.SOH;

    constructor(helper) {
        this.helper = helper; // SerialPortHelper 實例
        this.seq = 0;
    }

    async transfer(fileData, fileName, progressCb, finishCb, logCb) {
        this.logCb = logCb || console.log;
        this.progressCb = progressCb || (() => { });
        this.finishCb = finishCb || (() => { });

        if (!this.helper?.isConnected) throw new Error("串口未連線");

        await this.helper.switchMode("binary");
        this.logCb("🚀 開始 YModem 傳輸流程");

        try {
            // Step 1️⃣ 檔案標頭
            const header = this._buildFileHeader(fileName, fileData.length);
            this.logCb("📦 發送檔案標頭中...");

            let headerOk = false;
            for (let retry = 0; retry < 5 && !headerOk; retry++) {
                headerOk = await this._sendPacket(header, false, false, true, true);
                if (!headerOk) {
                    this.logCb(`🔁 標頭重試第 ${retry + 1} 次`);
                    // await new Promise(r => setTimeout(r, 1000));
                }
            }
            if (!headerOk) throw new Error("標頭傳送失敗 (ACK 未收到)");
            this.logCb("📦 檔案標頭已發送並確認 (ACK + C)");
            // Step 2️⃣ 傳輸資料
            let offset = 0;
            // let size = fileData.length;
            while (offset < fileData.length) {
                const chunk = fileData.slice(offset, offset + WebYModem.PACKET_LEN);
                const isLast = offset + WebYModem.PACKET_LEN >= fileData.length;
                let success = false;
                // let delay = size > 10000 ? 20 : 5;
                for (let retry = 0; retry < 10 && !success; retry++) {
                    success = await this._sendPacket(chunk, isLast, false, false, false);
                    if (!success) {
                        this.logCb(`🔁 重試第 ${retry + 1} 次`);
                        // delay *= 1.5;
                    }
                    // await new Promise(resolve => setTimeout(resolve, delay));
                }

                if (!success) throw new Error("封包傳送失敗，已重試10次");

                offset += WebYModem.PACKET_LEN;
                this.progressCb({ current: Math.min(offset, fileData.length), total: fileData.length });
            }

            // Step 3️⃣ 傳送 EOT
            await this._sendEOT();

            // Step 4️⃣ 結束封包（空封包）
            await this._sendEnd();

            this.logCb("🏁 傳輸完成 size:", fileData.length);
            this.finishCb();
        } catch (err) {
            this.logCb(`❌ 錯誤: ${err.message} size:`, fileData.length);
            this.finishCb("cancel");
        }

    }


    // _sendPacket 加入小延遲確保接收端能處理
    async _sendPacket(data, isLast = false, isEndHeader = false, isHeader = false, waitForC = false) {
        const packet = this._buildPacket(data, isLast, isEndHeader, isHeader);

        // 分段送出 (避免 USB CDC buffer 滿)
        for (let i = 0; i < packet.length; i += WebYModem.HEADER_TOTAL_LEN) {
            await this.helper.writeBinary(packet.slice(i, i + WebYModem.HEADER_TOTAL_LEN));
            await new Promise(r => setTimeout(r, 10));
        }

        // 等 ACK
        const firstResp = await this.helper.readBinaryByte(5000);
        if (firstResp === null) return false;
        const val = firstResp instanceof Uint8Array ? firstResp[0] : firstResp;

        if (val === WebYModem.ACK) {
            if (this.seq + 1 > 255) throw new Error("檔案過大");
            this.seq = (this.seq + 1) % 256;
            if (waitForC) {
                this.logCb("⏳ 等待接收端發出 'C' ...");
                const nextResp = await this.helper.readBinaryByte(5000);
                if (nextResp && nextResp[0] === WebYModem.C)
                    this.logCb("✅ 收到 'C'，進入下一階段");
            }
            return true;
        }
        if (val === WebYModem.NAK) return false;
        if (val === WebYModem.CAN) throw new Error("接收端取消");
        return false;
    }



    _buildPacket(data, isLast = false, isEndHeader = false, isHeader = false) {
        // const len = isHeader ? WebYModem.HEADER_LEN : WebYModem.PACKET_LEN;
        const len = WebYModem.HEADER_LEN;
        const totalLen = len + 5; // 包含 5 bytes 的標頭和 CRC
        // const first = isHeader ? WebYModem.SOH : WebYModem.STX;
        const first = WebYModem.SOH;
        const packet = new Uint8Array(totalLen);
        packet[0] = first;
        packet[1] = isEndHeader ? 0x00 : this.seq;
        packet[2] = isEndHeader ? 0xFF : 0xFF - this.seq;

        const dataBytes = new Uint8Array(len);
        dataBytes.set(data);
        if (isLast) {
            for (let i = data.length; i < dataBytes.length; i++) {
                dataBytes[i] = 0x1A;
            }
        }

        packet.set(dataBytes, 3);

        let crcHi = 0;
        let crcLow = 0;
        if (!isEndHeader) {
            const crc = this._calcCRC16(dataBytes);
            crcHi = (crc >> 8) & 0xFF;
            crcLow = crc & 0xFF;
        }

        // 結尾兩位：CRC High / Low
        packet[totalLen - 2] = crcHi;
        packet[totalLen - 1] = crcLow;

        return packet;
    }

    _buildFileHeader(fileName, size) {
        const encoder = new TextEncoder();
        const fileHeader = encoder.encode(`${fileName}\0${size}\0`);
        this.logCb("📦 檔案標頭內容: ", new TextDecoder().decode(fileHeader));
        return fileHeader;
    }

    async _sendEOT() {
        for (let i = 0; i < 5; i++) {
            await new Promise(resolve => setTimeout(resolve, 50));

            await this.helper.writeBinary(new Uint8Array([WebYModem.EOT]));
            const resp = await this.helper.readBinaryByte(3000);
            this.logCb("✅ EOT resp:", resp);

            const val = resp instanceof Uint8Array ? resp[0] : resp;

            if (val === WebYModem.ACK) {
                this.logCb("✅ EOT 被 ACK");
                // 等待接收端發出 'C' 要求結尾 header
                const next = await this.helper.readBinaryByte(3000);
                if (next && (next[0] === WebYModem.C || next === WebYModem.C)) {
                    this.logCb("📩 收到 C，準備傳送空 header 結束傳輸");
                    return true;
                }
                return true; // 沒有再收到 C 也不致命，接收端可能自動結束
            }

            await new Promise(r => setTimeout(r, 100));
        }

        throw new Error("EOT 未被 ACK");
    }
    async _sendEnd() {
        // 建立一個空 header（檔名空），header 長度 1 byte data (0x00) 會被 _buildPacket 填滿 128 bytes
        const headerData = new Uint8Array([0x00]);
        const packet = this._buildPacket(headerData, false, true, true); // isEndHeader = false -> 會算 CRC

        for (let i = 0; i < 5; i++) {
            await new Promise(resolve => setTimeout(resolve, 50));
            await this.helper.writeBinary(packet);

            const resp = await this.helper.readBinaryByte(3000);
            const val = resp instanceof Uint8Array ? resp[0] : resp;

            this.logCb("✅ END resp:", val);

            if (val === WebYModem.ACK) {
                this.logCb("✅ END 被 ACK，傳輸完成");
                return true;
            }

            await new Promise(r => setTimeout(r, 100 * (i + 1)));
        }
        throw new Error("END 未被 ACK");
    }

    _calcCRC16(bytes) {
        let crc = 0x0000;
        const poly = 0x1021;
        for (let j = 0; j < bytes.length; j++) {
            let b = bytes[j];
            for (let i = 0; i < 8; i++) {
                const bit = ((b >> (7 - i)) & 1) === 1;
                const c15 = ((crc >> 15) & 1) === 1;
                crc <<= 1;
                if (c15 ^ bit) crc ^= poly;
            }
        }
        return crc & 0xFFFF;
    }
}



// ======================================================
// ✅ SerialPortHelper 與 UsbCmdHelper 原本程式碼完整保留
// ======================================================
export class SerialPortHelper {
    constructor() {
        this.port = null;
        this.writer = null;
        this.reader = null;
        this.encoder = null;
        this.decoder = null;
        this.isConnected = false;
        this.buffer = "";
        this.handlers = {};
        this.mode = null; // "text" 或 "binary"
    }

    checkSupport() {
        if (!navigator.serial) {
            console.error("❌ Web Serial API 不支援此瀏覽器");
            return false;
        }
        return true;
    }

    async getPorts() {
        if (!this.checkSupport()) return [];
        return navigator.serial.getPorts();
    }

    async requestPort() {
        if (!this.checkSupport()) return null;
        return navigator.serial.requestPort();
    }

    /** 連接串口 */
    async connect(port) {
        if (!port) {
            console.error("❌ 沒有選擇串口裝置");
            return false;
        }
        try {
            // await port.open({ baudRate: 9600 });
            this.port = port;
            this.isConnected = true;

            // 預設文字模式
            await this.switchMode("text");

            console.log("✅ 已連接到串口裝置");
            return true;
        } catch (error) {
            console.error("❌ 連接串口裝置時發生錯誤:", error);
            return false;
        }
    }

    /** 切換模式：text / binary */
    async switchMode(mode = "text") {
        if (!this.port) return false;

        console.log("🔄 切換模式:", mode);
        await this._releaseAll();
        await new Promise(r => setTimeout(r, 200));

        if (!this.port.readable || !this.port.writable) {
            try {
                await this.port.open({ baudRate: this.baudRate || 115200 });
                console.log("✅ 重新開啟 port");
            } catch (err) {
                console.error("❌ 重新開啟 port 失敗:", err);
                return false;
            }
        }

        if (mode === "text") {
            this.encoder = new TextEncoderStream();
            this.decoder = new TextDecoderStream();

            // pipeTo 回傳 Promise，可用 catch 防止中斷報錯
            this.encoder.readable.pipeTo(this.port.writable).catch(() => { });
            this.port.readable.pipeTo(this.decoder.writable).catch(() => { });

            this.writer = this.encoder.writable.getWriter();
            this.reader = this.decoder.readable.getReader();
            this.readData();

            console.log("🔁 已切換為 text 模式");
        } else if (mode === "binary") {
            this.writer = this.port.writable.getWriter();
            this.reader = this.port.readable.getReader();
            console.log("🔁 已切換為 binary 模式");
        } else {
            throw new Error("未知模式: " + mode);
        }

        this.mode = mode;
        this.isConnected = true;

        return true;
    }

    /** 釋放 writer/reader */
    async _releaseAll() {
        try {
            if (this.reader) {
                await this.reader.cancel().catch(() => { });
                this.reader.releaseLock?.();
            }
        } catch (e) {
            console.warn("⚠️ reader cancel failed:", e);
        }

        try {
            if (this.writer) {
                await this.writer.close().catch(() => { });
                this.writer.releaseLock?.();
            }
        } catch (e) {
            console.warn("⚠️ writer close failed:", e);
        }

        // 強制中斷任何 pipeTo
        try {
            if (this.encoder?.readable?.locked) {
                console.warn("⚠️ encoder readable locked，嘗試中斷 pipe");
                this.encoder?.readable.cancel?.().catch(() => { });
            }
            if (this.decoder?.writable?.locked) {
                console.warn("⚠️ decoder writable locked，嘗試中斷 pipe");
                this.decoder?.writable?.abort?.().catch(() => { });
            }
        } catch (e) {
            console.warn("⚠️ encoder/decoder cancel failed:", e);
        }

        // 嘗試關閉 port
        try {
            if (this.port?.readable?.locked || this.port?.writable?.locked) {
                console.warn("⚠️ port locked，等待釋放");
                await new Promise(r => setTimeout(r, 200));
            }
            await this.port?.close?.();
            console.log("✅ port 已關閉");
        } catch (err) {
            console.warn("⚠️ 關閉 port 失敗:", err);
        }

        this.writer = this.reader = this.encoder = this.decoder = null;
        this.mode = null;
    }


    /** 寫入文字或 JSON */
    async writeData(payload, callback) {
        if (!this.isConnected || this.mode !== "text") {
            console.error("❌ 文字模式未啟動");
            return false;
        }
        try {
            if (callback && typeof payload === "object" && payload.cmd) {
                this.handlers[payload.cmd] = callback;
            }
            const dataToWrite =
                typeof payload === "object" ? JSON.stringify(payload) : payload;
            console.log("writeData payload ", payload)
            await this.writer.write(dataToWrite);
            return true;
        } catch (err) {
            console.error("❌ 發送文字資料錯誤:", err);
            return false;
        }
    }

    /** 寫入二進位資料（YModem） */
    async writeBinary(buffer) {
        if (!this.isConnected) {
            console.error("❌ 二進位模式未啟動");
            return false;
        }
        try {
            console.log('writeBinary ', buffer)
            await this.writer.write(buffer);
            return true;
        } catch (err) {
            console.error("❌ 發送二進位資料錯誤:", err);
            return false;
        }
    }

    async setDisconnectCallback(callback) {
        console.log('setDisconnectCallback callback ', callback)
        this.disConnectCallback = callback;
    }
    /** 讀取文字 */
    async readData() {
        if (!this.isConnected) return;
        try {
            while (this.isConnected) {
                const { value, done } = await this.reader.read();
                // console.log('readData value ', value)
                if (done) break;
                if (value) {
                    this.buffer += value;
                    this._processBuffer();
                }
            }
        } catch (err) {
            console.error("❌ 讀取資料錯誤:", err.message);
            if (err.message.includes('The device has been lost') && this.disConnectCallback) {
                this.disConnectCallback();
            }
        }
    }

    /** 讀取二進位資料（單 byte） */
    async readBinaryByte(timeout = 1000) {
        if (!this.reader) throw new Error("reader 尚未初始化");

        const timer = setTimeout(() => {
            this.reader.cancel().catch(() => { });
        }, timeout);

        try {
            const { value, done } = await this.reader.read();
            console.log('readBinaryByte value ', value)
            clearTimeout(timer);
            if (done || !value || value.length === 0) return null;

            // ✅ 只取第一個 byte（通常是控制碼）
            const firstByte = value[0];

            // 🩹 額外：如果後面是文字，可略過或記錄
            if (value.length > 1) {
                const text = new TextDecoder().decode(value.slice(1));
                console.warn("⚠️ 接收到額外資料:", text);
            }
            console.log('readBinaryByte firstByte ', value)

            return firstByte;
        } catch (err) {
            clearTimeout(timer);
            console.error("readBinaryByte 錯誤:", err.message);
            if (err.message.includes('The device has been lost') && this.disConnectCallback) {
                this.disConnectCallback();
            }
            return null;
        }
    }

    async disconnect() {
        await this._releaseAll();
        if (this.port) await this.port.close().catch(() => { });
        this.port = null;
        this.isConnected = false;
        console.log("🔌 串口已斷開");
    }

    _processBuffer() {
        while (this.buffer.includes("#$]")) {
            const start = this.buffer.indexOf("#$[");
            const end = this.buffer.indexOf("#$]");
            if (start === -1 || end === -1) break;
            const jsonStr = this.buffer.substring(start + 3, end);
            this.buffer = this.buffer.slice(end + 4);
            try {
                const data = JSON.parse(jsonStr);
                if (data.cmd && this.handlers[data.cmd])
                    this.handlers[data.cmd](data);
            } catch (e) {
                console.error("⚠️ JSON 解析失敗:", e, jsonStr);
            }
        }
    }

    onCmd(cmd, callback) { this.handlers[cmd] = callback; }
}



// ======================================================
// ✅ UsbCmdHelper 原本程式碼完整保留
// ======================================================
class UsbCmdHelper {
    constructor() { this.helper = new SerialPortHelper(); }

    async post(cmdKey, callback) {
        const cmd = UsbCmd[cmdKey] || cmdKey;
        return this.helper.writeData(cmd, callback);
    }

    _bindCommands() {
        Object.keys(UsbCmd).forEach(key => { this[key] = (data, callback) => this.post(key, callback); });
    }

    async requestPort() { return await this.helper.requestPort(); }
    async connect(port) { return await this.helper.connect(port); }
    async readData() { return await this.helper.readData(); }
    async setDisconnectCallback(callback) {
        return await this.helper.setDisconnectCallback(callback);
    }
    async disconnect() { return await this.helper.disconnect(); }

    // ✅ 新增 WebYModem 傳送方法
    async writeScriptStart(json, setPercentage, endCallback) {

        console.log("writeScriptStart json: ", json);
        const cmd = UsbCmd.scriptWrite;
        const cmdOk = await this.helper.writeData(cmd);
        console.log('writeScriptStart', "cmdOk: ", cmdOk);
        if (!cmdOk) return false;
        await new Promise(resolve => setTimeout(resolve, 2000)); // 等待裝置準備
        let scriptBuff;
        if (typeof json === "string") {
            // 將 JSON 字串先轉成 UTF-8 bytes
            // const utf8Bytes = new TextEncoder().encode(json);
            // 再轉成 base64 字串
            // const base64String = btoa(String.fromCharCode(...utf8Bytes));
            // 最後轉回 Uint8Array
            console.log('writeScriptStart', "json string: ", json);
            scriptBuff = new TextEncoder().encode(json);;
        } else if (json instanceof Uint8Array) {
            scriptBuff = json;
        } else if (json instanceof ArrayBuffer) {
            scriptBuff = new Uint8Array(json);
        } else {
            throw new Error("Invalid data type for YModem transfer");
        }

        console.log('writeScriptStart', "scriptBuff: ", scriptBuff);
        // 📊 傳輸進度回調
        const progressCallback = function (val) {
            let percentage = Math.round((100 * val.current) / val.total);
            if (percentage > 100) percentage = 100;
            console.log('link_logs', "percentage: ", percentage);
            if (setPercentage) setPercentage(percentage, taskType.DOWNLOAD_SCRIPT)
        }.bind(this);

        // ✅ 傳輸完成回調
        const finishCallback = async function (val) {
            console.log('finishCallback val ', val);
            this.helper.switchMode("text");
            await new Promise(resolve => setTimeout(resolve, 3000));
            endCallback(val);
        }.bind(this);

        // // 🪵 日誌
        const logCallback = console.log;
        const modem = new WebYModem(this.helper);
        await modem.transfer(
            scriptBuff,
            "testname.json",
            progressCallback,
            finishCallback,
            logCallback
        );
    }

    onCmd(cmd, callback) { return this.helper.onCmd(cmd, callback); }
}

// ======================================================
// ✅ 建立 Singleton 實例
// ======================================================
const Usb = new UsbCmdHelper();
Usb._bindCommands();
window.Usb = Usb;

export { Usb };
export default Usb;
